import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import ImageCropModal from '../../Components/utils/ImageCropModel';

const ImageUpload = ({
  onImagesChange,
  initialImages = [],
  initialThumbnail
}) => {

  const [showModal, setShowModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState(() => 
  initialImages?.map(image => ({
    url: image.url,
    file: image.file, 
    name: 'Existing Image',
    isExisting: true
  })) || []
);

useEffect(() => {
  const newSelectedImages = initialImages?.map(image => ({
    url: image.url,
    file: image.file,
    name: 'Existing Image',
    isExisting: true
  })) || [];

  // Only update state if the new value is different
  if (JSON.stringify(newSelectedImages) !== JSON.stringify(selectedImages)) {
    setSelectedImages(newSelectedImages);
  }
}, [initialImages]);

  const [selectedImageIndex, setSelectedImageIndex] = useState(() => {
    const index = initialImages.findIndex(image => image.url === initialThumbnail);
    return index >= 0 ? index : 0;
  });
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [filesToCrop, setFilesToCrop] = useState([]);
  const [tempImage, setTempImage] = useState(null);

  // Handle image changes and notify parent
  useEffect(() => {
    const thumbnail = selectedImages[selectedImageIndex]?.url || null;
    onImagesChange({
      images: selectedImages?.map(img => ({
        url: img.url,
        file: img.file,
        isExisting: img.isExisting
      })),
      thumbnail
    });
  }, [selectedImages, selectedImageIndex]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file =>
      file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024
    );

    const toCrop = [];
    const valid = [];
    let pendingChecks = validFiles.length;

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const w = img.width;
          const h = img.height;
          const ratio = w / h;

          if (Math.abs(ratio - 1) > 0.01) { // Check if not 1:1
            toCrop.push({ file, dataUrl: reader.result });
          } else {
            valid.push({
              url: URL.createObjectURL(file),
              file,
              name: file.name,
              isExisting: false
            });
          }

          pendingChecks--;
          if (!pendingChecks) {
            if (valid.length) {
              setSelectedImages(prev => [...prev, ...valid].slice(0, 6));
            }
            if (toCrop.length) {
              setFilesToCrop(toCrop);
              setTempImage(toCrop[0].dataUrl);
              setCropModalOpen(true);
            }
          }
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

    e.target.value = null;
  };

  const handleCropComplete = (croppedFile) => {
    const newImage = {
      url: URL.createObjectURL(croppedFile),
      file: croppedFile,
      name: filesToCrop[0]?.file?.name,
      isExisting: false
    };

    setSelectedImages(prev => [...prev, newImage].slice(0, 6));

    const updatedFilesToCrop = filesToCrop.slice(1);
    setFilesToCrop(updatedFilesToCrop);

    if (updatedFilesToCrop.length > 0) {
      setTempImage(updatedFilesToCrop[0].dataUrl);
    } else {
      setCropModalOpen(false);
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setFilesToCrop([]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => {
      const updated = prev.filter((_, i) => i !== index);
      if (prev[index]?.isExisting === false) {
        URL.revokeObjectURL(prev[index].url);
      }
      return updated;
    });
    setSelectedImageIndex(prev => Math.max(prev - 1, 0));
  };

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      selectedImages.forEach(img => {
        if (!img.isExisting) URL.revokeObjectURL(img.url);
      });
    };
  }, []);

  const MediaModal = () => (
    <div className="mediaModalOverlay">
      <div className="mediaModal">
        <div className="mediaModalHeader">
          <p className="modalCounter">
            {selectedImages.length > 0 ? `${selectedImageIndex + 1} OF ${selectedImages.length}` : 'Add Images'}
          </p>
          <button
            onClick={() => setShowModal(false)}
            className="closeButton"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        <div className="mediaContent">
          <div className="mainPreview">
            {selectedImages.length > 0 ? (
              <img
                src={selectedImages[selectedImageIndex].url}
                alt={`Selected ${selectedImageIndex + 1}`}
                className="mainPreviewImage"
              />
            ) : (
              <div className="emptyMainPreview">
                <label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hiddenInput"
                  />
                  <Plus size={48} color="#999" />
                </label>
              </div>
            )}
          </div>

          <div className="thumbnailGrid">
            {selectedImages?.map((image, index) => (
              <div
                key={index}
                className={`thumbnailContainer ${selectedImageIndex === index ? 'selected' : ''}`}
              >
                <img
                  src={image.url}
                  alt={`Preview ${index + 1}`}
                  className="thumbnailImage"
                  onClick={() => setSelectedImageIndex(index)}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(index);
                  }}
                  className="removeButton"
                  aria-label={`Remove image ${index + 1}`}
                >
                  <Trash2 size={16} color="#ff4081" />
                </button>
              </div>
            ))}
            {selectedImages.length < 6 && (
              <label className="uploadPlaceholder">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hiddenInput"
                />
                <Plus size={24} color="#999" />
                <span>Add</span>
              </label>
            )}
          </div>
        </div>

        <div className="modalActions">
          <button
            onClick={() => setShowModal(false)}
            className="primaryBtn2"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="imageUploadContainer">
      {selectedImages.length > 0 ? (
        <div className="imageGrid">
          {selectedImages?.map((image, index) => (
            <div key={index} className="imageGridItem" onClick={() => setShowModal(true)}>
              <img
                src={image.url}
                alt={`Preview ${index + 1}`}
                className="thumbnailImage"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveImage(index);
                }}
                className="removeButton"
                aria-label={`Remove image ${index + 1}`}
              >
                <Trash2 size={16} color="#ff4081" />
              </button>
            </div>
          ))}
          {selectedImages.length < 6 && (
            <button
              onClick={() => setShowModal(true)}
              className="addImageButton"
              aria-label="Add more images"
            >
              <Plus size={24} color="#999" />
            </button>
          )}
        </div>
      ) : (
        <div
          className="imageUploadBox"
          onClick={() => setShowModal(true)}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter') setShowModal(true);
          }}
        >
          <Plus size={24} color="#ff4081" />
        </div>
      )}

      {showModal && <MediaModal />}
      {cropModalOpen && (
        <ImageCropModal
          imageSrc={tempImage}
          open={cropModalOpen}
          onCancel={handleCropCancel}
          onComplete={handleCropComplete}
          aspect={1}
        />
      )}
    </div>
  );
};

export default ImageUpload;